import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  ClipboardList, Plus, Edit, Trash2, Eye, EyeOff, Clock, Target,
  CheckCircle, XCircle, Users, TrendingUp, FileText, Play, Send,
  HelpCircle, Award, Timer, BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExamManagement = () => {
  const [activeTab, setActiveTab] = useState('exams');
  const [loading, setLoading] = useState(true);
  const [exams, setExams] = useState([]);
  const [courses, setCourses] = useState([]);
  
  // Exam Dialog
  const [examDialogOpen, setExamDialogOpen] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [examForm, setExamForm] = useState({
    title: '',
    course_id: '',
    course_name: '',
    description: '',
    time_per_question: 60,
    max_attempts: 1,
    passing_percentage: 40,
    is_batch_exam: false,
    scheduled_start: '',
    scheduled_end: ''
  });
  
  // Question Builder State
  const [selectedExam, setSelectedExam] = useState(null);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_option: 'A',
    marks: 1,
    explanation: ''
  });
  
  // Results State
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [examResults, setExamResults] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [examsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/exams`),
        axios.get(`${API}/admin/courses`).catch(() => ({ data: [] }))
      ]);
      setExams(examsRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch exam data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Exam Handlers
  const handleCreateExam = async (e) => {
    e.preventDefault();
    try {
      const course = courses.find(c => c.id === examForm.course_id);
      const data = {
        ...examForm,
        course_name: course?.name || examForm.course_name,
        time_per_question: parseInt(examForm.time_per_question),
        max_attempts: parseInt(examForm.max_attempts),
        passing_percentage: parseFloat(examForm.passing_percentage)
      };
      
      if (editingExam) {
        await axios.put(`${API}/exams/${editingExam.id}`, data);
        toast.success('Exam updated successfully');
      } else {
        await axios.post(`${API}/exams`, data);
        toast.success('Exam created successfully');
      }
      setExamDialogOpen(false);
      resetExamForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save exam');
    }
  };

  const resetExamForm = () => {
    setEditingExam(null);
    setExamForm({
      title: '',
      course_id: '',
      course_name: '',
      description: '',
      time_per_question: 60,
      max_attempts: 1,
      passing_percentage: 40,
      is_batch_exam: false,
      scheduled_start: '',
      scheduled_end: ''
    });
  };

  const openEditExam = (exam) => {
    setEditingExam(exam);
    setExamForm({
      title: exam.title,
      course_id: exam.course_id,
      course_name: exam.course_name,
      description: exam.description || '',
      time_per_question: exam.time_per_question || 60,
      max_attempts: exam.max_attempts || 1,
      passing_percentage: exam.passing_percentage || 40,
      is_batch_exam: exam.is_batch_exam || false,
      scheduled_start: exam.scheduled_start ? exam.scheduled_start.slice(0, 16) : '',
      scheduled_end: exam.scheduled_end ? exam.scheduled_end.slice(0, 16) : ''
    });
    setExamDialogOpen(true);
  };

  const togglePublish = async (examId) => {
    try {
      const res = await axios.put(`${API}/exams/${examId}/publish`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle publish status');
    }
  };

  const deleteExam = async (examId) => {
    if (!window.confirm('Are you sure? This will delete all questions too.')) return;
    try {
      await axios.delete(`${API}/exams/${examId}`);
      toast.success('Exam deleted');
      fetchData();
      if (selectedExam?.id === examId) setSelectedExam(null);
    } catch (error) {
      toast.error('Failed to delete exam');
    }
  };

  // Question Handlers
  const loadExamQuestions = async (examId) => {
    try {
      const res = await axios.get(`${API}/exams/${examId}`);
      setSelectedExam(res.data);
      setActiveTab('builder');
    } catch (error) {
      toast.error('Failed to load exam details');
    }
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    try {
      if (editingQuestion) {
        await axios.put(`${API}/exams/${selectedExam.id}/questions/${editingQuestion.id}`, questionForm);
        toast.success('Question updated');
      } else {
        await axios.post(`${API}/exams/${selectedExam.id}/questions`, questionForm);
        toast.success('Question added');
      }
      setQuestionDialogOpen(false);
      resetQuestionForm();
      loadExamQuestions(selectedExam.id);
    } catch (error) {
      toast.error('Failed to save question');
    }
  };

  const resetQuestionForm = () => {
    setEditingQuestion(null);
    setQuestionForm({
      question_text: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      correct_option: 'A',
      marks: 1,
      explanation: ''
    });
  };

  const openEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      question_text: question.question_text,
      option_a: question.option_a,
      option_b: question.option_b,
      option_c: question.option_c,
      option_d: question.option_d,
      correct_option: question.correct_option,
      marks: question.marks || 1,
      explanation: question.explanation || ''
    });
    setQuestionDialogOpen(true);
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await axios.delete(`${API}/exams/${selectedExam.id}/questions/${questionId}`);
      toast.success('Question deleted');
      loadExamQuestions(selectedExam.id);
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  // Results Handlers
  const viewResults = async (examId) => {
    try {
      const res = await axios.get(`${API}/exams/${examId}/results`);
      setExamResults(res.data || []);
      setResultsDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load results');
    }
  };

  const releaseResults = async (examId) => {
    try {
      await axios.post(`${API}/exams/${examId}/release-results`);
      toast.success('Results released to all students');
    } catch (error) {
      toast.error('Failed to release results');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exam-management">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="exams" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            All Exams
          </TabsTrigger>
          <TabsTrigger value="builder" className="flex items-center gap-2" disabled={!selectedExam}>
            <FileText className="h-4 w-4" />
            Question Builder {selectedExam && `(${selectedExam.title})`}
          </TabsTrigger>
        </TabsList>

        {/* Exams List Tab */}
        <TabsContent value="exams" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Online Exams</h3>
            <Dialog open={examDialogOpen} onOpenChange={setExamDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={resetExamForm}
                  data-testid="create-exam-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Exam
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingExam ? 'Edit Exam' : 'Create New Exam'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateExam} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Exam Title *</Label>
                      <Input
                        value={examForm.title}
                        onChange={(e) => setExamForm({...examForm, title: e.target.value})}
                        placeholder="Enter exam title"
                        required
                        data-testid="exam-title-input"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Course *</Label>
                      <select
                        className="w-full p-2 border rounded-md"
                        value={examForm.course_id}
                        onChange={(e) => {
                          const course = courses.find(c => c.id === e.target.value);
                          setExamForm({
                            ...examForm, 
                            course_id: e.target.value,
                            course_name: course?.name || ''
                          });
                        }}
                        required
                        data-testid="exam-course-select"
                      >
                        <option value="">Select Course</option>
                        {courses.map(course => (
                          <option key={course.id} value={course.id}>{course.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-span-2">
                      <Label>Description</Label>
                      <textarea
                        className="w-full p-2 border rounded-md"
                        value={examForm.description}
                        onChange={(e) => setExamForm({...examForm, description: e.target.value})}
                        placeholder="Exam description"
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label>Time per Question (seconds) *</Label>
                      <Input
                        type="number"
                        value={examForm.time_per_question}
                        onChange={(e) => setExamForm({...examForm, time_per_question: e.target.value})}
                        min="10"
                        max="600"
                        required
                        data-testid="time-per-question-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Default: 60 sec = 1 min per question
                      </p>
                    </div>
                    
                    <div>
                      <Label>Max Attempts</Label>
                      <Input
                        type="number"
                        value={examForm.max_attempts}
                        onChange={(e) => setExamForm({...examForm, max_attempts: e.target.value})}
                        min="1"
                        max="10"
                        data-testid="max-attempts-input"
                      />
                    </div>
                    
                    <div>
                      <Label>Passing Percentage (%)</Label>
                      <Input
                        type="number"
                        value={examForm.passing_percentage}
                        onChange={(e) => setExamForm({...examForm, passing_percentage: e.target.value})}
                        min="0"
                        max="100"
                        data-testid="passing-percentage-input"
                      />
                    </div>
                    
                    <div className="flex items-center gap-2 pt-6">
                      <input
                        type="checkbox"
                        id="is_batch_exam"
                        checked={examForm.is_batch_exam}
                        onChange={(e) => setExamForm({...examForm, is_batch_exam: e.target.checked})}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="is_batch_exam" className="cursor-pointer">
                        Batch Exam (Release results together)
                      </Label>
                    </div>
                    
                    {examForm.is_batch_exam && (
                      <>
                        <div>
                          <Label>Scheduled Start</Label>
                          <Input
                            type="datetime-local"
                            value={examForm.scheduled_start}
                            onChange={(e) => setExamForm({...examForm, scheduled_start: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Scheduled End</Label>
                          <Input
                            type="datetime-local"
                            value={examForm.scheduled_end}
                            onChange={(e) => setExamForm({...examForm, scheduled_end: e.target.value})}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setExamDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-exam-btn">
                      {editingExam ? 'Update Exam' : 'Create Exam'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{exams.length}</p>
              <p className="text-sm text-blue-700">Total Exams</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {exams.filter(e => e.is_published).length}
              </p>
              <p className="text-sm text-green-700">Published</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">
                {exams.reduce((sum, e) => sum + (e.total_questions || 0), 0)}
              </p>
              <p className="text-sm text-purple-700">Total Questions</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-orange-600">
                {exams.filter(e => e.is_batch_exam).length}
              </p>
              <p className="text-sm text-orange-700">Batch Exams</p>
            </div>
          </div>

          {/* Exams Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-3 text-sm font-semibold">Exam Title</th>
                  <th className="p-3 text-sm font-semibold">Course</th>
                  <th className="p-3 text-sm font-semibold">Questions</th>
                  <th className="p-3 text-sm font-semibold">Time/Q</th>
                  <th className="p-3 text-sm font-semibold">Pass %</th>
                  <th className="p-3 text-sm font-semibold">Type</th>
                  <th className="p-3 text-sm font-semibold">Status</th>
                  <th className="p-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {exams.map((exam) => (
                  <tr key={exam.id} className="hover:bg-slate-50" data-testid={`exam-row-${exam.id}`}>
                    <td className="p-3">
                      <div>
                        <p className="font-medium">{exam.title}</p>
                        <p className="text-xs text-slate-500">{exam.description?.slice(0, 50)}</p>
                      </div>
                    </td>
                    <td className="p-3 text-sm">{exam.course_name}</td>
                    <td className="p-3 text-sm font-medium">{exam.total_questions || 0}</td>
                    <td className="p-3 text-sm">
                      <span className="flex items-center gap-1">
                        <Timer className="h-3 w-3" />
                        {exam.time_per_question}s
                      </span>
                    </td>
                    <td className="p-3 text-sm">{exam.passing_percentage}%</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        exam.is_batch_exam ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {exam.is_batch_exam ? 'Batch' : 'Individual'}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        exam.is_published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {exam.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => loadExamQuestions(exam.id)}
                          title="Edit Questions"
                          data-testid={`edit-questions-btn-${exam.id}`}
                        >
                          <HelpCircle className="h-3 w-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openEditExam(exam)}
                          title="Edit Exam"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => togglePublish(exam.id)}
                          title={exam.is_published ? 'Unpublish' : 'Publish'}
                        >
                          {exam.is_published ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => viewResults(exam.id)}
                          title="View Results"
                          data-testid={`view-results-btn-${exam.id}`}
                        >
                          <BarChart3 className="h-3 w-3" />
                        </Button>
                        {exam.is_batch_exam && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => releaseResults(exam.id)}
                            title="Release Results"
                            className="text-green-600"
                          >
                            <Send className="h-3 w-3" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteExam(exam.id)}
                          title="Delete Exam"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {exams.length === 0 && (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-slate-500">
                      <ClipboardList className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      <p>No exams created yet. Click "Create Exam" to get started.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Question Builder Tab */}
        <TabsContent value="builder" className="space-y-4">
          {selectedExam ? (
            <>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold">{selectedExam.title}</h3>
                  <p className="text-sm text-slate-500">
                    {selectedExam.course_name} | {selectedExam.questions?.length || 0} Questions | Total Marks: {selectedExam.total_marks || 0}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setActiveTab('exams')}>
                    Back to Exams
                  </Button>
                  <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={resetQuestionForm}
                        data-testid="add-question-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Question
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add MCQ Question'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddQuestion} className="space-y-4">
                        <div>
                          <Label>Question Text *</Label>
                          <textarea
                            className="w-full p-2 border rounded-md"
                            value={questionForm.question_text}
                            onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                            placeholder="Enter the question"
                            rows={3}
                            required
                            data-testid="question-text-input"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="flex items-center gap-2">
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">A</span>
                              Option A *
                            </Label>
                            <Input
                              value={questionForm.option_a}
                              onChange={(e) => setQuestionForm({...questionForm, option_a: e.target.value})}
                              required
                              data-testid="option-a-input"
                            />
                          </div>
                          <div>
                            <Label className="flex items-center gap-2">
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">B</span>
                              Option B *
                            </Label>
                            <Input
                              value={questionForm.option_b}
                              onChange={(e) => setQuestionForm({...questionForm, option_b: e.target.value})}
                              required
                              data-testid="option-b-input"
                            />
                          </div>
                          <div>
                            <Label className="flex items-center gap-2">
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">C</span>
                              Option C *
                            </Label>
                            <Input
                              value={questionForm.option_c}
                              onChange={(e) => setQuestionForm({...questionForm, option_c: e.target.value})}
                              required
                              data-testid="option-c-input"
                            />
                          </div>
                          <div>
                            <Label className="flex items-center gap-2">
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">D</span>
                              Option D *
                            </Label>
                            <Input
                              value={questionForm.option_d}
                              onChange={(e) => setQuestionForm({...questionForm, option_d: e.target.value})}
                              required
                              data-testid="option-d-input"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Correct Answer *</Label>
                            <select
                              className="w-full p-2 border rounded-md"
                              value={questionForm.correct_option}
                              onChange={(e) => setQuestionForm({...questionForm, correct_option: e.target.value})}
                              required
                              data-testid="correct-option-select"
                            >
                              <option value="A">A</option>
                              <option value="B">B</option>
                              <option value="C">C</option>
                              <option value="D">D</option>
                            </select>
                          </div>
                          <div>
                            <Label>Marks</Label>
                            <Input
                              type="number"
                              value={questionForm.marks}
                              onChange={(e) => setQuestionForm({...questionForm, marks: parseFloat(e.target.value)})}
                              min="0.5"
                              step="0.5"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label>Explanation (shown after exam)</Label>
                          <textarea
                            className="w-full p-2 border rounded-md"
                            value={questionForm.explanation}
                            onChange={(e) => setQuestionForm({...questionForm, explanation: e.target.value})}
                            placeholder="Explain the correct answer"
                            rows={2}
                          />
                        </div>
                        
                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => setQuestionDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-question-btn">
                            {editingQuestion ? 'Update Question' : 'Add Question'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Questions List */}
              <div className="space-y-4">
                {selectedExam.questions?.map((q, idx) => (
                  <div key={q.id} className="bg-slate-50 rounded-lg p-4" data-testid={`question-${q.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">
                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-sm font-bold mr-2">
                            Q{idx + 1}
                          </span>
                          {q.question_text}
                        </p>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                          <div className={`p-2 rounded border ${q.correct_option === 'A' ? 'bg-green-50 border-green-300' : 'bg-white'}`}>
                            <span className="font-bold text-sm">A.</span> {q.option_a}
                            {q.correct_option === 'A' && <CheckCircle className="inline h-4 w-4 text-green-600 ml-2" />}
                          </div>
                          <div className={`p-2 rounded border ${q.correct_option === 'B' ? 'bg-green-50 border-green-300' : 'bg-white'}`}>
                            <span className="font-bold text-sm">B.</span> {q.option_b}
                            {q.correct_option === 'B' && <CheckCircle className="inline h-4 w-4 text-green-600 ml-2" />}
                          </div>
                          <div className={`p-2 rounded border ${q.correct_option === 'C' ? 'bg-green-50 border-green-300' : 'bg-white'}`}>
                            <span className="font-bold text-sm">C.</span> {q.option_c}
                            {q.correct_option === 'C' && <CheckCircle className="inline h-4 w-4 text-green-600 ml-2" />}
                          </div>
                          <div className={`p-2 rounded border ${q.correct_option === 'D' ? 'bg-green-50 border-green-300' : 'bg-white'}`}>
                            <span className="font-bold text-sm">D.</span> {q.option_d}
                            {q.correct_option === 'D' && <CheckCircle className="inline h-4 w-4 text-green-600 ml-2" />}
                          </div>
                        </div>
                        {q.explanation && (
                          <p className="text-sm text-slate-500 mt-2 italic">
                            Explanation: {q.explanation}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-2">Marks: {q.marks}</p>
                      </div>
                      <div className="flex gap-1 ml-4">
                        <Button size="sm" variant="outline" onClick={() => openEditQuestion(q)}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-600"
                          onClick={() => deleteQuestion(q.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {(!selectedExam.questions || selectedExam.questions.length === 0) && (
                  <div className="text-center py-12 text-slate-500">
                    <HelpCircle className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    <p>No questions added yet. Click "Add Question" to start building your exam.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
              <p>Select an exam from the Exams tab to edit questions.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Results Dialog */}
      <Dialog open={resultsDialogOpen} onOpenChange={setResultsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Exam Results</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-3">Student</th>
                  <th className="p-3">Submitted</th>
                  <th className="p-3">Correct</th>
                  <th className="p-3">Wrong</th>
                  <th className="p-3">Marks</th>
                  <th className="p-3">Percentage</th>
                  <th className="p-3">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {examResults.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="p-3">
                      <div>
                        <p className="font-medium">{r.student_name}</p>
                        <p className="text-xs text-slate-500">{r.student_email}</p>
                      </div>
                    </td>
                    <td className="p-3 text-slate-600">
                      {r.end_time ? new Date(r.end_time).toLocaleString() : '-'}
                    </td>
                    <td className="p-3 text-green-600 font-medium">{r.correct_answers}</td>
                    <td className="p-3 text-red-600 font-medium">{r.wrong_answers}</td>
                    <td className="p-3 font-medium">{r.marks_obtained}/{r.total_marks}</td>
                    <td className="p-3 font-medium">{r.percentage?.toFixed(1)}%</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        r.result === 'pass' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {r.result?.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))}
                {examResults.length === 0 && (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-slate-500">
                      No attempts yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExamManagement;
